'use client';

import { useQuery } from '@tanstack/react-query';

// Cache ETH price for 60 seconds
const PRICE_CACHE_TIME = 60 * 1000;
const PRICE_STALE_TIME = 30 * 1000;

// Fallback price if server endpoint is unavailable
const FALLBACK_ETH_PRICE = 1900;

/**
 * Fetch ETH price from the server-cached API endpoint.
 * The server caches the Envio result for 60s so all clients share one query.
 */
async function fetchEthPrice(): Promise<number> {
  try {
    const response = await fetch('/api/rpc/eth-price');
    if (!response.ok) {
      return FALLBACK_ETH_PRICE;
    }

    const json = await response.json();
    return json.data?.priceUsd ?? FALLBACK_ETH_PRICE;
  } catch (error) {
    console.error('[useEthPrice] Failed to fetch ETH price:', error);
    return FALLBACK_ETH_PRICE;
  }
}

/**
 * Hook to get the current ETH price in USD.
 * Fetches from a server-cached endpoint (backed by Envio indexer).
 * 
 * @returns ETH price data and loading state
 */
export function useEthPrice() {
  const { data: ethPrice, isLoading, error } = useQuery({
    queryKey: ['eth-price'],
    queryFn: fetchEthPrice,
    staleTime: PRICE_STALE_TIME,
    refetchInterval: PRICE_CACHE_TIME,
    // Inherit global retry policy (no retry on middleware 429s,
    // jittered backoff on upstream rate limits). `fetchEthPrice`
    // already swallows network errors with a fallback price, so
    // even on a hard failure the UI keeps rendering.
  });

  return {
    /** Current ETH price in USD */
    ethPrice: ethPrice ?? FALLBACK_ETH_PRICE,
    /** Whether the price is still loading */
    isLoading,
    /** Any error from fetching */
    error,
  };
}

/**
 * Convert ETH (in wei) to USD value.
 * 
 * @param weiAmount Amount in wei (18 decimals)
 * @param ethPrice Current ETH price in USD
 * @returns USD value as a number
 */
export function ethToUsd(weiAmount: bigint, ethPrice: number): number {
  const ethAmount = Number(weiAmount) / 1e18;
  return ethAmount * ethPrice;
}
